import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { Provider } from 'react-redux';
import { PersistGate } from 'redux-persist/integration/react';
import Login from './components/Login';
import Dashboard from './components/Dashboard';
import Stats from './components/Stats';
import Groups from './components/Groups';
import CreateGroup from './components/CreateGroup';
import EditGroup from './components/EditGroup';
import Students from './components/Students';
import CreateStudent from './components/CreateStudent';
import EditStudent from './components/EditStudent';
import Lessons from './components/Lessons';
import LessonEditor from './components/LessonEditor';
import Journal from './components/Journal';
import Vocabulary from './components/Vocabulary';
import PublicVocabulary from './components/PublicVocabulary';
import Certificates from './components/Certificates';
import Courses from './components/Courses';
import CourseEditor from './components/CourseEditor';
import CreateCertificate from './components/CreateCertificate';
import Settings from './components/Settings';
import AdminLayout from './components/AdminLayout';
import LoadingSpinner from './components/LoadingSpinner';
import PreventNumberInputScroll from './components/PreventNumberInputScroll';
import { store, persistor } from './store/store';
import { useAppSelector, useAppDispatch } from './store/hooks';
import { fetchCurrentUserAsync, initializeAuth } from './store/slices/authSlice';
import Absences from './components/Absences';
import Ads from './components/Ads';
import Consultations from './components/Consultations';
import CreateAd from './components/CreateAd';
import EditAd from './components/EditAd';
import CreateConsultation from './components/CreateConsultation';
import EditConsultation from './components/EditConsultation';
import RecordingUpload from './components/RecordingUpload';
import RecordingDetail from './components/RecordingDetail';
import Recordings from './components/Recordings';
import Vacations from './components/Vacations';
import VacationForm from './components/VacationForm';
import Subscriptions from './components/Subscriptions';
import ExpiringSubscriptions from './components/ExpiringSubscriptions';
import CreateSubscription from './components/CreateSubscription';
import StudentVacations from './components/StudentVacations';
import StudentVacationForm from './components/StudentVacationForm';
import EditSubscription from './components/EditSubscription';
import Contacts from './components/Contacts';
import ReelsGuide from './components/ReelsGuide';
import Accounting from './components/Accounting';
import AccountingStats from './components/AccountingStats';
import MentionRewards from './components/MentionRewards';
import MentionRewardForm from './components/MentionRewardForm';
import Giveaways from './components/Giveaways';
import GiveawayForm from './components/GiveawayForm';
import './App.css';

const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated } = useAppSelector((state) => state.auth);
  
  return isAuthenticated ? <>{children}</> : <Navigate to="/login" />;
};

const AppContent: React.FC = () => {
  const dispatch = useAppDispatch();
  const { token, user } = useAppSelector((state) => state.auth);

  useEffect(() => {
    // Initialize auth state to reset any stuck loading states
    dispatch(initializeAuth());
    
    // If we have a token but no user, fetch the current user
    if (token && !user) {
      dispatch(fetchCurrentUserAsync());
    }
  }, [dispatch, token, user]);

  return (
    <Router>
      <div className="App">
        <PreventNumberInputScroll />
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/p/vocabulary" element={<PublicVocabulary />} />
          <Route 
            path="/dashboard" 
            element={
              <ProtectedRoute>
                <AdminLayout>
                  <Dashboard />
                </AdminLayout>
              </ProtectedRoute>
            } 
          />
          <Route
            path="/stats"
            element={
              <ProtectedRoute>
                <AdminLayout>
                  <Stats />
                </AdminLayout>
              </ProtectedRoute>
            }
          />
          <Route 
            path="/groups" 
            element={
              <ProtectedRoute>
                <AdminLayout>
                  <Groups />
                </AdminLayout>
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/groups/create" 
            element={
              <ProtectedRoute>
                <AdminLayout>
                  <CreateGroup />
                </AdminLayout>
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/groups/edit/:id" 
            element={
              <ProtectedRoute>
                <AdminLayout>
                  <EditGroup />
                </AdminLayout>
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/students" 
            element={
              <ProtectedRoute>
                <AdminLayout>
                  <Students />
                </AdminLayout>
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/students/create" 
            element={
              <ProtectedRoute>
                <AdminLayout>
                  <CreateStudent />
                </AdminLayout>
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/students/edit/:id" 
            element={
              <ProtectedRoute>
                <AdminLayout>
                  <EditStudent />
                </AdminLayout>
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/lessons" 
            element={
              <ProtectedRoute>
                <AdminLayout>
                  <Lessons />
                </AdminLayout>
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/courses" 
            element={
              <ProtectedRoute>
                <AdminLayout>
                  <Courses />
                </AdminLayout>
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/courses/create" 
            element={
              <ProtectedRoute>
                <AdminLayout>
                  <CourseEditor />
                </AdminLayout>
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/courses/edit/:id" 
            element={
              <ProtectedRoute>
                <AdminLayout>
                  <CourseEditor />
                </AdminLayout>
              </ProtectedRoute>
            } 
          />
          <Route
            path="/lesson"
            element={
              <ProtectedRoute>
                <AdminLayout>
                  <LessonEditor />
                </AdminLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/journal"
            element={
              <ProtectedRoute>
                <AdminLayout>
                  <Journal />
                </AdminLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/accounting"
            element={
              <ProtectedRoute>
                <AdminLayout>
                  <Accounting />
                </AdminLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/accounting/stats"
            element={
              <ProtectedRoute>
                <AdminLayout>
                  <AccountingStats />
                </AdminLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/vocabulary"
            element={
              <ProtectedRoute>
                <AdminLayout>
                  <Vocabulary />
                </AdminLayout>
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/certificates" 
            element={
              <ProtectedRoute>
                <AdminLayout>
                  <Certificates />
                </AdminLayout>
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/absences" 
            element={
              <ProtectedRoute>
                <AdminLayout>
                  <Absences />
                </AdminLayout>
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/ads" 
            element={
              <ProtectedRoute>
                <AdminLayout>
                  <Ads />
                </AdminLayout>
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/ads/create" 
            element={
              <ProtectedRoute>
                <AdminLayout>
                  <CreateAd />
                </AdminLayout>
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/ads/edit/:id" 
            element={
              <ProtectedRoute>
                <AdminLayout>
                  <EditAd />
                </AdminLayout>
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/consultations" 
            element={
              <ProtectedRoute>
                <AdminLayout>
                  <Consultations />
                </AdminLayout>
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/consultations/create" 
            element={
              <ProtectedRoute>
                <AdminLayout>
                  <CreateConsultation />
                </AdminLayout>
              </ProtectedRoute>
            } 
          />
          <Route
            path="/consultations/edit/:id"
            element={
              <ProtectedRoute>
                <AdminLayout>
                  <EditConsultation />
                </AdminLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/consultations/:consultationId/recordings/new"
            element={
              <ProtectedRoute>
                <AdminLayout>
                  <RecordingUpload />
                </AdminLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/recordings"
            element={
              <ProtectedRoute>
                <AdminLayout>
                  <Recordings />
                </AdminLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/recordings/:id"
            element={
              <ProtectedRoute>
                <AdminLayout>
                  <RecordingDetail />
                </AdminLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/contacts"
            element={
              <ProtectedRoute>
                <AdminLayout>
                  <Contacts />
                </AdminLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/marketing/reels"
            element={
              <ProtectedRoute>
                <AdminLayout>
                  <ReelsGuide />
                </AdminLayout>
              </ProtectedRoute>
            }
          />
          <Route 
            path="/certificates/create" 
            element={
              <ProtectedRoute>
                <AdminLayout>
                  <CreateCertificate />
                </AdminLayout>
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/vacations" 
            element={
              <ProtectedRoute>
                <AdminLayout>
                  <Vacations />
                </AdminLayout>
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/vacations/create" 
            element={
              <ProtectedRoute>
                <AdminLayout>
                  <VacationForm />
                </AdminLayout>
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/vacations/edit/:id" 
            element={
              <ProtectedRoute>
                <AdminLayout>
                  <VacationForm />
                </AdminLayout>
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/student-vacations" 
            element={
              <ProtectedRoute>
                <AdminLayout>
                  <StudentVacations />
                </AdminLayout>
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/student-vacations/create" 
            element={
              <ProtectedRoute>
                <AdminLayout>
                  <StudentVacationForm />
                </AdminLayout>
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/student-vacations/edit/:studentId/:vacationId" 
            element={
              <ProtectedRoute>
                <AdminLayout>
                  <StudentVacationForm />
                </AdminLayout>
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/subscriptions" 
            element={
              <ProtectedRoute>
                <AdminLayout>
                  <Subscriptions />
                </AdminLayout>
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/subscriptions/edit/:studentId/:subscriptionId" 
            element={
              <ProtectedRoute>
                <AdminLayout>
                  <EditSubscription />
                </AdminLayout>
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/subscriptions/create" 
            element={
              <ProtectedRoute>
                <AdminLayout>
                  <CreateSubscription />
                </AdminLayout>
              </ProtectedRoute>
            } 
          />
          <Route
            path="/expiring-subscriptions"
            element={
              <ProtectedRoute>
                <AdminLayout>
                  <ExpiringSubscriptions />
                </AdminLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/giveaways"
            element={
              <ProtectedRoute>
                <AdminLayout>
                  <Giveaways />
                </AdminLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/giveaways/create"
            element={
              <ProtectedRoute>
                <AdminLayout>
                  <GiveawayForm />
                </AdminLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/giveaways/edit/:id"
            element={
              <ProtectedRoute>
                <AdminLayout>
                  <GiveawayForm />
                </AdminLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/mentions"
            element={
              <ProtectedRoute>
                <AdminLayout>
                  <MentionRewards />
                </AdminLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/mentions/create"
            element={
              <ProtectedRoute>
                <AdminLayout>
                  <MentionRewardForm />
                </AdminLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/mentions/edit/:studentId/:subscriptionId"
            element={
              <ProtectedRoute>
                <AdminLayout>
                  <MentionRewardForm />
                </AdminLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/settings"
            element={
              <ProtectedRoute>
                <AdminLayout>
                  <Settings />
                </AdminLayout>
              </ProtectedRoute>
            }
          />
          <Route path="/" element={<Navigate to="/dashboard" />} />
        </Routes>
      </div>
    </Router>
  );
};

const App: React.FC = () => {
  return (
    <Provider store={store}>
      <PersistGate loading={<LoadingSpinner />} persistor={persistor}>
        <AppContent />
      </PersistGate>
    </Provider>
  );
};

export default App;
